import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollText, Filter as FilterIcon, X } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

export default function AuditPage() {
  const [data, setData] = useState({ total: 0, items: [], filters: { actions: [], actors: [], targets: [] } });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ action: "all", actor: "all", target: "all", date_from: "", date_to: "" });
  const [expanded, setExpanded] = useState(null);

  const load = async (resetPage = false) => {
    setLoading(true);
    try {
      const p = resetPage ? 0 : page;
      const params = { limit: PAGE_SIZE, skip: p * PAGE_SIZE };
      if (filters.action !== "all") params.action = filters.action;
      if (filters.actor !== "all") params.actor = filters.actor;
      if (filters.target !== "all") params.target_collection = filters.target;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await api.get("/audit-logs", { params });
      setData(data);
      if (resetPage) setPage(0);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(true); }, [filters.action, filters.actor, filters.target, filters.date_from, filters.date_to]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, [page]);

  const hasActiveFilter = filters.action !== "all" || filters.actor !== "all" || filters.target !== "all" || filters.date_from || filters.date_to;
  const clearFilters = () => setFilters({ action: "all", actor: "all", target: "all", date_from: "", date_to: "" });
  const totalPages = Math.ceil((data.total || 0) / PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="audit-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Audit Trail</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Log Aktivitas</h1>
        <p className="text-slate-600 text-sm mt-2">Catatan tidak terhapus dari semua perubahan sistem. Total {data.total.toLocaleString("id-ID")} entri.</p>
      </header>

      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
              <FilterIcon className="h-3.5 w-3.5" /> Filter
            </div>
            {hasActiveFilter && (
              <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="clear-filters-button">
                <X className="h-3.5 w-3.5 mr-1" /> Hapus filter
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
              <SelectTrigger className="h-11" data-testid="filter-action"><SelectValue placeholder="Aksi" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Semua aksi</SelectItem>
                {data.filters.actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.actor} onValueChange={(v) => setFilters({ ...filters, actor: v })}>
              <SelectTrigger className="h-11" data-testid="filter-actor"><SelectValue placeholder="Aktor" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Semua pengguna</SelectItem>
                {data.filters.actors.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.target} onValueChange={(v) => setFilters({ ...filters, target: v })}>
              <SelectTrigger className="h-11" data-testid="filter-target"><SelectValue placeholder="Entitas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua entitas</SelectItem>
                {data.filters.targets.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-11" data-testid="filter-date-from" />
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-11" data-testid="filter-date-to" />
          </div>
        </CardContent>
      </Card>

      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Waktu</th>
              <th className="text-left px-4 py-3 font-semibold">Pengguna</th>
              <th className="text-left px-4 py-3 font-semibold">Aksi</th>
              <th className="text-left px-4 py-3 font-semibold">Target</th>
              <th className="text-left px-4 py-3 font-semibold">Detail</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && !loading && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                <ScrollText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                {hasActiveFilter ? "Tidak ada entri yang cocok filter." : "Belum ada aktivitas."}
              </td></tr>
            )}
            {data.items.map((log) => (
              <tr key={log.id} className="border-t border-slate-200 align-top" data-testid={`audit-row-${log.id}`}>
                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                  {new Date(log.created_at).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "medium" })}
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold">{log.username}</div>
                  <div className="text-xs text-slate-500">{ROLE_LABEL[log.role] || log.role}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-xs font-semibold font-mono">
                    {log.action}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <div className="text-xs font-medium">{log.target_collection || "-"}</div>
                  <div className="font-mono text-[10px] text-slate-400 truncate max-w-xs">{log.target_id || ""}</div>
                </td>
                <td className="px-4 py-3">
                  {(log.before || log.after) ? (
                    <button onClick={() => setExpanded(expanded === log.id ? null : log.id)} className="text-xs text-blue-700 hover:underline" data-testid={`expand-${log.id}`}>
                      {expanded === log.id ? "Sembunyikan" : "Lihat"}
                    </button>
                  ) : <span className="text-xs text-slate-400">-</span>}
                  {expanded === log.id && (
                    <pre className="mt-2 bg-slate-50 border border-slate-200 rounded p-2 text-[10px] whitespace-pre-wrap break-all max-w-md max-h-48 overflow-auto">{JSON.stringify({ before: log.before, after: log.after }, null, 2)}</pre>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm" data-testid="audit-pager">
          <div className="text-slate-600">Halaman <b>{page + 1}</b> dari <b>{totalPages}</b></div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="pager-prev">← Sebelumnya</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} data-testid="pager-next">Berikutnya →</Button>
          </div>
        </div>
      )}
    </div>
  );
}
